import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { MapContainer, TileLayer, Polyline, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { CalendarDays, ChevronDown, ChevronUp, Loader2, MapPin, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROUTE_COLORS = ['#3B82F6', '#8B5CF6', '#06B6D4', '#EC4899', '#14B8A6', '#F43F5E', '#F59E0B', '#22C55E'];

const dotIcon = (color) => L.divIcon({
  className: '',
  html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

const UserRouteMap = ({ routes }) => {
  const allPts = routes.flatMap((r) => r.route);
  if (allPts.length < 2) return <p className="text-xs text-gray-400 py-3">No GPS points recorded.</p>;
  const bounds = L.latLngBounds(allPts.map(([lat, lng]) => [lat, lng]));
  return (
    <div className="h-72 rounded-lg overflow-hidden border" data-testid="today-stats-route-map">
      <MapContainer bounds={bounds} boundsOptions={{ padding: [20, 20] }} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
        {routes.map((r, i) => {
          const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
          return (
            <React.Fragment key={i}>
              <Polyline positions={r.route} pathOptions={{ color, weight: 3, opacity: 0.85 }} />
              <Marker position={r.route[0]} icon={dotIcon(color)} />
              <Marker position={r.route[r.route.length - 1]} icon={dotIcon('#111827')} />
            </React.Fragment>
          );
        })}
      </MapContainer>
    </div>
  );
};

const UserRow = ({ user, daysAgo = 0, tid = 'today-stats' }) => {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [routes, setRoutes] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && routes === null) {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/today-stats/${user.user_id}/routes?days_ago=${daysAgo}`, getAuthHeader());
        setRoutes(res.data.routes || []);
      } catch {
        setRoutes([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        data-testid={`${tid}-user-${user.user_id}`}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900 text-sm">{user.name}</span>
        <span className="flex items-center gap-3">
          <span className="text-sm font-bold text-orange-600">{user.count.toLocaleString()} flyers</span>
          {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-gray-50 border-t space-y-3">
          <div className="flex flex-wrap gap-2 pt-2">
            {user.jobs.map((j) => (
              <span key={j.job_id} className="text-[11px] bg-white border rounded-full px-2.5 py-1 text-gray-600">
                {j.title}: <span className="font-semibold text-gray-900">{j.count}</span>
              </span>
            ))}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-3">
              <Loader2 className="animate-spin" size={14} /> Loading GPS routes…
            </div>
          ) : routes && routes.length > 0 ? (
            <>
              <UserRouteMap routes={routes} />
              <div className="flex flex-wrap gap-3">
                {routes.map((r, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-[11px] text-gray-600">
                    <span className="w-3 h-1 rounded" style={{ backgroundColor: ROUTE_COLORS[i % ROUTE_COLORS.length] }}></span>
                    {r.job_title} ({r.source === 'session' ? 'session' : 'live'})
                  </span>
                ))}
              </div>
            </>
          ) : routes ? (
            <p className="text-xs text-gray-400 py-2">No GPS route recordings for today.</p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export const TodayStats = ({ daysAgo = 0, title = "Today's Stats", emptyText = 'No deliveries recorded yet today.' }) => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tid = daysAgo === 0 ? 'today-stats' : `day-${daysAgo}-stats`;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/today-stats?days_ago=${daysAgo}`, getAuthHeader());
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAgo]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <Card className="p-6" data-testid={`${tid}-section`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays size={20} className={daysAgo === 0 ? 'text-orange-500' : 'text-indigo-500'} />
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {data && <span className="text-xs text-gray-400">{data.date} (Central Time)</span>}
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <span className="text-sm text-gray-500">
              Total: <span className="font-bold text-gray-900" data-testid={`${tid}-total`}>{data.total.toLocaleString()}</span> flyers
            </span>
          )}
          <button type="button" onClick={() => { setLoading(true); fetchStats(); }} data-testid={`${tid}-refresh-btn`}
            className="text-gray-400 hover:text-gray-600" title="Refresh">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
          <Loader2 className="animate-spin" size={16} /> Loading stats…
        </div>
      ) : !data || data.users.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm flex flex-col items-center gap-2">
          <MapPin size={22} />
          {emptyText}
        </div>
      ) : (
        <div className="space-y-2">
          {data.users.map((u) => <UserRow key={u.user_id} user={u} daysAgo={daysAgo} tid={tid} />)}
        </div>
      )}
    </Card>
  );
};

export default TodayStats;
